/**
 * Where a wall is drawn, taken from the desktop (Unity) editor's wall prefabs
 * (`Assets/MikuEditor/Prefabs/Obstacles/*Wrap.prefab`). Every wall type — Left, Right, Center, the two
 * Diagonals, Crouch, Square, Triangle — is just a wall: its x and y always come from the file, and its
 * type decides only the model and a fixed offset between that position and where the model sits.
 *
 * In each prefab the root sits at the file position and the visible shape — which is also the node
 * the wall's z-rotation is applied to (`Track.cs` `AddMovementGameObjectToScene` rotates
 * `GetChild(0)`) — sits at a fixed offset from it. So a wall is drawn at `file position + offset` and
 * rotated about that point. Builds before this drew Left/Right/Center/Diagonal walls at the raw file
 * position in world units (not × 1.365 like everything else) and replaced any position within 0.15 of
 * the origin — the format's own centre among them — with a preference default (CAND-AF).
 *
 * Offsets and scales are note-space (the file's units). The models are the same FBX files the desktop
 * editor uses, byte for byte, authored in centimetres (FBX `UnitScaleFactor` 1.0), which Unity imports
 * at 0.01; `DESKTOP_MODEL_SCALE` is that 0.01 and is the shipped global `obstacleScale`.
 */
import { GRID_CENTRE, NOTE_TO_WORLD } from './placementGeometry';

export type WallKind =
  | 'wall_left' | 'wall_right' | 'center' | 'angle_left' | 'angle_right' | 'crouch' | 'square' | 'triangle';

/** Unity's import scale for these centimetre-unit models, and the shipped global `obstacleScale`. */
export const DESKTOP_MODEL_SCALE = 0.01;

/**
 * Per wall: the offset of the visible shape from the file position, the scale on the shape (product
 * of the prefab's scale chain, X and Y), and its fixed rotation. Read from the prefabs' transforms.
 */
export const DESKTOP_WALLS: Record<WallKind, { offset: [number, number]; scale: [number, number]; rotation: number }> = {
  wall_left:   { offset: [-0.54, 0.1],   scale: [0.5487891, 0.77],        rotation: 0 },  // LeftWrap › Cube
  wall_right:  { offset: [0.552, 0.1],   scale: [0.5487891, 0.77],        rotation: 0 },  // RightWrap › Cube
  center:      { offset: [0, 0.15],      scale: [1, 1],                   rotation: 0 },  // CenterWrap › Cube
  angle_left:  { offset: [-0.339, 0.14], scale: [0.8, 0.8],               rotation: 0 },  // DiagonalLeftWrap › RotationWrap (0.8) › Cube (1)
  angle_right: { offset: [0.339, 0.14],  scale: [0.8, 0.8],               rotation: 0 },  // DiagonalRighttWrap › RotationWrap (0.8) › Cube (1)
  crouch:      { offset: [0, 1.033],     scale: [0.7558578, 0.7368389],   rotation: 90 }, // CrouchWrap › Cube (rotated 90°)
  square:      { offset: [0, -0.25],     scale: [2.59 * 0.99504554, 2.59 * 0.99504554], rotation: 0 }, // SquareWrap › RotationWrap (2.59) › Cube (0.995)
  triangle:    { offset: [0, -0.63],     scale: [2.433, 2.433],           rotation: 0 },  // TriangleWrap › RotationWrap (2.433) › Cube (1)
};

/**
 * The Per-Obstacle Settings defaults that reproduce the desktop wall: Pos X / Pos Y are the offset in
 * world units, Scale X / Y the prefab scale in world units relative to `DESKTOP_MODEL_SCALE`, and
 * Rotation the prefab's own. Z scale is not derived — the web editor's depth axis is time at the
 * chart's BPM and note speed, which has no fixed relation to the desktop's 20 units per second.
 */
export function desktopWallDefaults(kind: WallKind): { defaultX: number; defaultY: number; scaleX: number; scaleY: number; rotation: number } {
  const w = DESKTOP_WALLS[kind];
  return {
    defaultX: w.offset[0] * NOTE_TO_WORLD,
    defaultY: w.offset[1] * NOTE_TO_WORLD,
    scaleX: w.scale[0] * NOTE_TO_WORLD,
    scaleY: w.scale[1] * NOTE_TO_WORLD,
    rotation: w.rotation,
  };
}

/** One rotation step, in degrees: a wall turns in 7.5° steps. */
export const WALL_ROTATION_STEP = 7.5;

/** A wall rotation wrapped into 0 <= r < 360. */
export function normalizeWallRotation(deg: number): number {
  const r = ((deg % 360) + 360) % 360;
  return r === 360 ? 0 : r;
}

/** A wall's file position in note space — the format's centre when the wall carries none. */
export function wallFileXY(position: ArrayLike<number> | null | undefined): [number, number] {
  return position && position.length >= 2 ? [position[0], position[1]] : [GRID_CENTRE[0], GRID_CENTRE[1]];
}

/**
 * Where a wall is drawn, in world units, with Y relative to `NOTE_Y_OFFSET` (as every caller adds it):
 * the file position in world units plus the type's Pos X / Pos Y. Nothing is ever substituted for the
 * file position.
 */
export function wallXY(position: ArrayLike<number> | null | undefined, posX: number, posY: number): [number, number] {
  const [x, y] = wallFileXY(position);
  return [x * NOTE_TO_WORLD + posX, y * NOTE_TO_WORLD + posY];
}

/** Two crouch records closer than this in time are the same crouch (the API derives both from one value). */
export const CROUCH_MATCH_MS = 0.5;

/**
 * Each crouch in the difficulty's crouch list, with the file position of its `crouch_obstacles` entry
 * (null when it has none). A crouch is a wall like any other — the desktop editor saves its position
 * from its transform (`Track.cs` `SaveChartAction`) — but the store keeps crouches as a list of times,
 * with the positions alongside in `crouch_obstacles`. Both lists are sorted by time, so one forward
 * walk pairs them; two crouches within `CROUCH_MATCH_MS` pair in order of appearance.
 */
export function crouchWallEntries(
  crouches: readonly number[],
  crouchObstacles: ReadonlyArray<{ time_ms: number; position?: number[] | null }> | null | undefined,
): Array<{ timeMs: number; position: number[] | null }> {
  const obs = crouchObstacles ?? [];
  const out: Array<{ timeMs: number; position: number[] | null }> = [];
  let j = 0;
  for (const timeMs of crouches) {
    while (j < obs.length && obs[j].time_ms < timeMs - CROUCH_MATCH_MS) j++;
    if (j < obs.length && Math.abs(obs[j].time_ms - timeMs) <= CROUCH_MATCH_MS) {
      out.push({ timeMs, position: obs[j].position ?? null });
      j++;
    } else {
      out.push({ timeMs, position: null });
    }
  }
  return out;
}

/** The note-space position a newly placed wall is given: the format's centre, at its own time. */
export function newWallPosition(timeMs: number): [number, number, number] {
  return [GRID_CENTRE[0], GRID_CENTRE[1], timeMs * 0.02];
}
